import { Component } from "@theme/component";

/** @typedef {any} SwiperInstance */

/**
 * 找到事件来源所属的 Swiper 容器。
 * @param {Event} event
 * @returns {Element | null}
 */
function findSwiperRoot(event) {
  const target = event.target;
  if (!(target instanceof Element)) return null;
  return target.closest(".swiper");
}

/**
 * 阻止轮播内的浏览器原生拖拽，避免打断 Swiper 的鼠标拖动状态。
 *
 * 背景：Swiper 默认的 `touchStartPreventDefault: true` 会在 mousedown 时
 * preventDefault，天然屏蔽原生拖拽；本组件为了保留移动端纵向滚动把它设为了
 * false，原生拖拽随之被放行。一旦拖拽会话启动，浏览器不再派发 pointerup/mouseup，
 * Swiper 就会一直停留在拖动状态，表现为「松开鼠标后轮播仍跟着指针走」。
 *
 * 这里不限定 img / a：文本选区、带 draggable 的元素、picture、svg 等
 * 同样能启动原生拖拽，只要来源在 .swiper 内就一律拦掉。
 * 使用事件委托以覆盖独立 Swiper 和运行时动态插入的 slide。
 * @param {DragEvent} event
 */
function preventNativeSwiperDrag(event) {
  if (findSwiperRoot(event)) {
    event.preventDefault();
  }
}

/**
 * 兜底：若原生拖拽仍被其他脚本放行，拖拽结束时补发指针抬起事件，
 * 强制 Swiper 退出拖动状态，避免残留「鼠标跟随」。
 * @param {DragEvent} event
 */
function releaseSwiperDragState(event) {
  if (!findSwiperRoot(event)) return;

  const { clientX, clientY } = event;
  const init = {
    bubbles: true,
    cancelable: true,
    clientX,
    clientY,
    view: window,
  };

  try {
    document.dispatchEvent(
      new PointerEvent("pointerup", { ...init, pointerId: 1, pointerType: "mouse" }),
    );
  } catch {
    // 少数环境不支持 PointerEvent 构造函数，交给下面的 mouseup 兜底。
  }
  document.dispatchEvent(new MouseEvent("mouseup", init));
}

document.addEventListener("dragstart", preventNativeSwiperDrag, true);
document.addEventListener("dragend", releaseSwiperDragState, true);
document.addEventListener("drop", releaseSwiperDragState, true);

/**
 * spx-slider - Swiper Web Component
 *
 * 通过 data-* 属性传参（不再支持 data-config JSON）。
 *
 * 支持属性（括号为默认值）:
 * - slidesPerViewMobile {number|'auto'} (default: 1)
 * - slidesPerViewPad {number|'auto'|null} (default: null, 未设置时 750px+ 使用 desktop)
 * - slidesPerViewDesktop {number|'auto'} (default: 3)
 * - spaceBetweenMobile {number} (default: 16)
 * - spaceBetweenDesktop {number} (default: 24)
 * - gridRowsMobile {number} (default: 1)
 * - gridRowsDesktop {number} (default: 1)
 * - gridFill {'column'|'row'} (default: 'column')
 * - slidesPerGroupMobile {number} (default: 1)
 * - slidesPerGroupPad {number|null} (default: null, 未设置时 pad 使用 desktop)
 * - slidesPerGroupDesktop {number} (default: 1)
 * - loop {boolean} (default: false)
 * - pagination {boolean} (default: true)
 * - navigation {boolean} (default: true)
 * - autoplay {boolean} (default: false)
 * - autoplayMobile {boolean} (default: autoplay)
 * - autoplayDesktop {boolean} (default: autoplay)
 * - autoplayDelay {number} (default: 5000)
 * - autoplayPauseOnHover {boolean} (default: false, data-autoplay-pause-on-hover)
 * - autoHeight {boolean} (default: false)
 * - touchMove {boolean} (default: true)
 * - simulateTouch {boolean} (default: true)
 * - overflow {string|null} (default: null)
 * - center {boolean} (default: false)
 * - initialSlide {number} (default: 0)
 * - onlyMobile {boolean} (default: false)
 * - onlyPad {boolean} (default: false)
 * - onlyPadExact {boolean} (default: false)
 * - onlyDesktop {boolean} (default: false)
 *
 * @example
 * <spx-slider
 *   data-slides-per-view-mobile="1"
 *   data-slides-per-view-desktop="3"
 *   data-space-between-mobile="16"
 *   data-space-between-desktop="24"
 *   data-autoplay="true"
 *   data-autoplay-delay="5000"
 *   data-only-mobile="true"
 * >
 *   <div class="swiper">
 *     <div class="swiper-wrapper">
 *       <div class="swiper-slide">...</div>
 *     </div>
 *     <div class="swiper-pagination"></div>
 *     <div class="swiper-button-prev"></div>
 *     <div class="swiper-button-next"></div>
 *   </div>
 * </spx-slider>
 */
export class SpxSlider extends Component {
  /** @type {SwiperInstance | null} */
  swiperInstance = null;
  /** @type {ResizeObserver | null} */
  resizeObserver = null;
  /** @type {MediaQueryList | null} */
  mobileMql = null;
  /** @type {(() => void) | null} */
  mobileMqlHandler = null;
  /** @type {MediaQueryList | null} */
  padMql = null;
  /** @type {(() => void) | null} */
  padMqlHandler = null;
  /** @type {MediaQueryList | null} */
  padExactMql = null;
  /** @type {(() => void) | null} */
  padExactMqlHandler = null;
  /** @type {MediaQueryList | null} */
  desktopMql = null;
  /** @type {(() => void) | null} */
  desktopMqlHandler = null;
  /** @type {IntersectionObserver | null} */
  intersectionObserver = null;
  /** @type {boolean} */
  isInViewport = false;

  connectedCallback() {
    super.connectedCallback();
    this.initObserver();
    this.initViewportListener();
    this.initIntersectionObserver();
    requestAnimationFrame(() => this.syncSwiperWithViewport());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    this.destroyViewportListener();
    this.destroyIntersectionObserver();
    this.destroySwiper();
  }

  getHeightRefElement() {
    // 优先尝试从 refs 数组中获取（针对 ref="height[]"）
    if (Array.isArray(this.refs.height) && this.refs.height.length > 0) {
      return this.refs.height[0];
    }
    // 其次尝试从 refs 对象中获取单个元素（针对 ref="height"）
    if (this.refs.height && !Array.isArray(this.refs.height)) {
      return this.refs.height;
    }
    // 最后尝试手动查询（作为后备）
    return this.querySelector('[ref^="height"]');
  }

  initObserver() {
    this.resizeObserver = new ResizeObserver(() => {
      this.handleResize();
    });
    // 监听整个组件的大小变化
    this.resizeObserver.observe(this);

    // 如果有 ref="height" 元素，也单独监听它
    const refEl = this.getHeightRefElement();
    if (refEl) {
      this.resizeObserver.observe(refEl);
    }
  }

  handleResize() {
    const config = this.getConfig();
    // 当 slider 只在特定断点生效时，非匹配视口直接隐藏导航/分页
    if (config.onlyMobile && !this.isMobileViewport()) {
      this.applyDisabledDesktopState();
      return;
    }

    if (!config.onlyMobile && config.onlyPad && !this.isPadViewport()) {
      this.applyDisabledDesktopState();
      return;
    }

    if (
      !config.onlyMobile &&
      !config.onlyPad &&
      config.onlyPadExact &&
      !this.isPadExactViewport()
    ) {
      this.applyDisabledDesktopState();
      return;
    }

    if (
      !config.onlyMobile &&
      !config.onlyPad &&
      !config.onlyPadExact &&
      config.onlyDesktop &&
      !this.isDesktopViewport()
    ) {
      this.applyDisabledDesktopState();
      return;
    }

    if (this.swiperInstance) {
      this.swiperInstance.update();
      if (this.swiperInstance.params?.loop) {
        this.swiperInstance.loopFix?.();
      }
      this.updateLockedVisibility(config, this.getElements());
    }
    this.updatePosition();
  }

  destroySwiper() {
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }
  }

  initSwiper() {
    // A tab can replace this slider before its deferred initialization runs.
    if (!this.isConnected) return;
    if (this.swiperInstance) return;

    const SwiperCtor =
      /** @type {{new (...args: any[]): SwiperInstance} | undefined} */ (
        /** @type {any} */ (window).Swiper
      );
    if (!SwiperCtor) {
      setTimeout(() => this.initSwiper(), 50);
      return;
    }

    const container = this.querySelector(".swiper");
    if (!container) return;

    let config = this.getConfig();
    const elements = this.getElements();

    const slideCount = this.getSlideCount(container);
    if (slideCount <= 1) {
      config = {
        ...config,
        autoplay: false,
        autoplayMobile: false,
        autoplayDesktop: false,
        loop: false,
        pagination: false,
        navigation: false,
      };
    }
    const shouldLoop = Boolean(config.loop) && slideCount > 1;
    const usesAutoSlidesPerView =
      config.slidesPerViewMobile === 'auto' ||
      config.slidesPerViewPad === 'auto' ||
      config.slidesPerViewDesktop === 'auto';
    // Swiper 12：loopedSlides 含 loopAdditionalSlides；过大则触发「slides 不足」误报（旧逻辑曾用 slideCount）。
    const loopAdditionalSlides = shouldLoop && usesAutoSlidesPerView ? 2 : 0;
    const mobileGridConfig =
      config.gridRowsMobile > 1 || config.gridRowsDesktop > 1
        ? {
            rows: Math.max(1, config.gridRowsMobile),
            fill: config.gridFill,
          }
        : undefined;
    const autoplayEnabled = this.hasAutoplay(config);

    const paginationConfig = config.pagination
      ? elements.pagination
        ? {
            el: elements.pagination,
            clickable: true,
          }
        : false
      : false;

    const navigationConfig = config.navigation
      ? elements.prev && elements.next
        ? {
            prevEl: elements.prev,
            nextEl: elements.next,
          }
        : false
      : false;

    this.applyOverflow(container, config.overflow);
    this.setupPagination(
      elements.pagination,
      config.responsiveAutoplay
        ? {
            ...config,
            autoplay: this.isAutoplayEnabledForViewport(config),
          }
        : config,
    );
    this.setupVisibility(elements, config);
    this.updatePosition();

    // 如果初始化时找到了 ref="height"，确保它被监听
    const refEl = this.getHeightRefElement();
    if (refEl && this.resizeObserver) {
      this.resizeObserver.observe(refEl);
    }

    try {
      this.swiperInstance = new SwiperCtor(container, {
        loop: shouldLoop,
        centeredSlides: config.center,
        autoHeight: config.autoHeight,
        allowTouchMove: config.touchMove,
        simulateTouch: config.simulateTouch,
        // Keep horizontal swipe responsive on mobile while preserving vertical page scroll.
        touchStartPreventDefault: false,
        initialSlide: config.initialSlide,
        slidesPerView: config.slidesPerViewMobile,
        spaceBetween: config.spaceBetweenMobile,
        ...(mobileGridConfig ? { grid: mobileGridConfig } : {}),
        ...(config.responsiveGrouping
          ? { slidesPerGroup: config.slidesPerGroupMobile }
          : {}),
        // watchOverflow 会在「无溢出」时锁定 Swiper，loop 无法从末尾切回开头；开启 loop 时必须关掉。
        watchOverflow: !shouldLoop,
        loopAdditionalSlides,
        autoplay: autoplayEnabled
          ? {
              delay: config.autoplayDelay,
              disableOnInteraction: false,
              pauseOnMouseEnter: Boolean(config.autoplayPauseOnHover),
              // 统一策略：自动播放默认关闭，仅在进入视口后由 IntersectionObserver 启动。
              enabled: false,
            }
          : undefined,
        pagination: paginationConfig,
        navigation: navigationConfig,
        on: this.getEvents(config, elements),
        breakpoints: this.getSwiperBreakpoints(config),
      });

      if (shouldLoop && typeof this.swiperInstance.slideToLoop === "function") {
        this.swiperInstance.slideToLoop(config.initialSlide, 0, false);
      }

      if (config.responsiveAutoplay) {
        this.syncResponsiveAutoplay(config);
      } else if (config.autoplay && this.swiperInstance.autoplay) {
        if (this.isInViewport) {
          this.swiperInstance.autoplay.start();
        } else {
          this.swiperInstance.autoplay.stop();
        }
      }

      this.updateLockedVisibility(config, elements);
      this.updatePosition();
    } catch (error) {
      console.error("[spx-slider] Swiper init failed:", error, this);
    }
  }

  /**
   * @param {any} config
   * @param {any} elements
   */
  getEvents(config, elements) {
    if (!config.responsiveAutoplay) {
      return config.autoplay ? this.getAutoplayEvents(elements.pagination) : {};
    }

    const autoplayEvents = this.hasAutoplay(config)
      ? this.getAutoplayEvents(elements.pagination) || {}
      : {};
    if (!this.hasAutoplay(config) || config.loop) return autoplayEvents;

    const slideChange = autoplayEvents.slideChange;
    return {
      ...autoplayEvents,
      reachEnd: (swiper) => {
        this.stopResponsiveAutoplayAtEnd(swiper, elements.next, config);
      },
      slideChange: (swiper) => {
        slideChange?.(swiper);
        this.stopResponsiveAutoplayAtEnd(swiper, elements.next, config);
      },
    };
  }

  get gridRowsMobile() {
    return this.parseValue(this.dataset.gridRowsMobile, 1);
  }

  get gridRowsDesktop() {
    return this.parseValue(this.dataset.gridRowsDesktop, 1);
  }

  get gridFill() {
    return this.dataset.gridFill || "column";
  }

  get slidesPerGroupMobile() {
    return this.parseValue(this.dataset.slidesPerGroupMobile, 1);
  }

  get slidesPerGroupPad() {
    const d = this.dataset;
    if (d.slidesPerGroupPad === undefined || d.slidesPerGroupPad === '') {
      return null;
    }
    return this.parseValue(d.slidesPerGroupPad, null);
  }

  get slidesPerGroupDesktop() {
    return this.parseValue(this.dataset.slidesPerGroupDesktop, 1);
  }

  get autoplayMobile() {
    const autoplay = this.parseValue(this.dataset.autoplay, false);
    return this.parseValue(this.dataset.autoplayMobile, autoplay);
  }

  get autoplayDesktop() {
    const autoplay = this.parseValue(this.dataset.autoplay, false);
    return this.parseValue(this.dataset.autoplayDesktop, autoplay);
  }

  get responsiveGrouping() {
    return (
      this.dataset.slidesPerGroupMobile !== undefined ||
      this.dataset.slidesPerGroupPad !== undefined ||
      this.dataset.slidesPerGroupDesktop !== undefined
    );
  }

  get responsiveAutoplay() {
    return (
      this.dataset.autoplayMobile !== undefined ||
      this.dataset.autoplayDesktop !== undefined
    );
  }

  getConfig() {
    const d = this.dataset;

    return {
      // slidesPerView 默认使用具体数值，若明确传入 "auto" 则按 Swiper 原生逻辑处理
      slidesPerViewMobile: this.parseValue(d.slidesPerViewMobile, 1),
      slidesPerViewPad:
        d.slidesPerViewPad === undefined || d.slidesPerViewPad === ''
          ? null
          : this.parseValue(d.slidesPerViewPad, null),
      slidesPerViewDesktop: this.parseValue(d.slidesPerViewDesktop, 3),
      spaceBetweenMobile: this.parseValue(d.spaceBetweenMobile, 16),
      spaceBetweenDesktop: this.parseValue(d.spaceBetweenDesktop, 24),
      gridRowsMobile: this.gridRowsMobile,
      gridRowsDesktop: this.gridRowsDesktop,
      gridFill: this.gridFill,
      slidesPerGroupMobile: this.slidesPerGroupMobile,
      slidesPerGroupPad: this.slidesPerGroupPad,
      slidesPerGroupDesktop: this.slidesPerGroupDesktop,
      responsiveGrouping: this.responsiveGrouping,
      loop: this.parseValue(d.loop, false),
      pagination: this.parseValue(d.pagination, true),
      navigation: this.parseValue(d.navigation, true),
      autoplay: this.parseValue(d.autoplay, false),
      autoplayMobile: this.autoplayMobile,
      autoplayDesktop: this.autoplayDesktop,
      responsiveAutoplay: this.responsiveAutoplay,
      autoplayDelay: this.parseValue(d.autoplayDelay, 5000),
      autoplayPauseOnHover: this.parseValue(d.autoplayPauseOnHover, false),
      autoHeight: this.parseValue(d.autoHeight, false),
      touchMove: this.parseValue(d.touchMove, true),
      simulateTouch: this.parseValue(d.simulateTouch, true),
      overflow: d.overflow || null,
      center: this.parseValue(d.center, false),
      initialSlide: this.parseValue(d.initialSlide, 0),
      onlyMobile: this.parseValue(d.onlyMobile, false),
      onlyPad: this.parseValue(d.onlyPad, false),
      onlyPadExact: this.parseValue(d.onlyPadExact, false),
      onlyDesktop: this.parseValue(d.onlyDesktop, false),
    };
  }

  getPadMinWidth() {
    return this.getConfig().slidesPerViewPad != null ? 768 : 750;
  }

  /**
   * @param {any} config
   */
  getSwiperBreakpoints(config) {
    const desktopGridConfig =
      config.gridRowsDesktop > 1 || config.gridRowsMobile > 1
        ? { rows: config.gridRowsDesktop, fill: config.gridFill }
        : undefined;
    const shared = {
      spaceBetween: config.spaceBetweenDesktop,
      centeredSlides: config.center,
      ...(desktopGridConfig ? { grid: desktopGridConfig } : {}),
      ...(config.responsiveGrouping
        ? { slidesPerGroup: config.slidesPerGroupDesktop }
        : {}),
    };

    if (config.slidesPerViewPad != null) {
      const padGroup =
        config.responsiveGrouping
          ? {
              slidesPerGroup:
                config.slidesPerGroupPad != null
                  ? config.slidesPerGroupPad
                  : config.slidesPerGroupDesktop,
            }
          : {};

      return {
        768: {
          slidesPerView: config.slidesPerViewPad,
          spaceBetween: config.spaceBetweenDesktop,
          centeredSlides: config.center,
          ...(desktopGridConfig ? { grid: desktopGridConfig } : {}),
          ...padGroup,
        },
        1025: {
          slidesPerView: config.slidesPerViewDesktop,
          ...shared,
        },
      };
    }

    return {
      750: {
        slidesPerView: config.slidesPerViewDesktop,
        ...shared,
      },
    };
  }

  isMobileViewport() {
    return window.matchMedia(`(max-width: ${this.getPadMinWidth() - 1}px)`).matches;
  }

  isPadViewport() {
    return window.matchMedia(`(min-width: ${this.getPadMinWidth()}px)`).matches;
  }

  isPadExactViewport() {
    return window.matchMedia(`(min-width: ${this.getPadMinWidth()}px) and (max-width: 1024px)`).matches;
  }

  isDesktopViewport() {
    return window.matchMedia('(min-width: 1025px)').matches;
  }

  initViewportListener() {
    const padMin = this.getPadMinWidth();
    this.mobileMql = window.matchMedia(`(max-width: ${padMin - 1}px)`);
    this.padMql = window.matchMedia(`(min-width: ${padMin}px)`);
    this.padExactMql = window.matchMedia(`(min-width: ${padMin}px) and (max-width: 1024px)`);
    this.desktopMql = window.matchMedia('(min-width: 1025px)');

    this.mobileMqlHandler = () => {
      this.syncSwiperWithViewport();
      this.syncResponsiveAutoplay();
    };

    this.padMqlHandler = () => {
      this.syncSwiperWithViewport();
      this.syncResponsiveAutoplay();
    };

    this.padExactMqlHandler = () => {
      this.syncSwiperWithViewport();
      this.syncResponsiveAutoplay();
    };

    this.desktopMqlHandler = () => {
      this.syncSwiperWithViewport();
      this.syncResponsiveAutoplay();
    };

    if (this.mobileMql.addEventListener) {
      this.mobileMql.addEventListener("change", this.mobileMqlHandler);
    } else {
      // @ts-ignore
      this.mobileMql.addListener(this.mobileMqlHandler);
    }

    if (this.padMql.addEventListener) {
      this.padMql.addEventListener("change", this.padMqlHandler);
    } else {
      // @ts-ignore
      this.padMql.addListener(this.padMqlHandler);
    }

    if (this.padExactMql.addEventListener) {
      this.padExactMql.addEventListener("change", this.padExactMqlHandler);
    } else {
      // @ts-ignore
      this.padExactMql.addListener(this.padExactMqlHandler);
    }

    if (this.desktopMql.addEventListener) {
      this.desktopMql.addEventListener("change", this.desktopMqlHandler);
    } else {
      // @ts-ignore
      this.desktopMql.addListener(this.desktopMqlHandler);
    }
  }

  destroyViewportListener() {
    if (this.mobileMql && this.mobileMqlHandler) {
      if (this.mobileMql.removeEventListener) {
        this.mobileMql.removeEventListener("change", this.mobileMqlHandler);
      } else {
        // @ts-ignore
        this.mobileMql.removeListener(this.mobileMqlHandler);
      }
    }

    if (this.padMql && this.padMqlHandler) {
      if (this.padMql.removeEventListener) {
        this.padMql.removeEventListener("change", this.padMqlHandler);
      } else {
        // @ts-ignore
        this.padMql.removeListener(this.padMqlHandler);
      }
    }

    if (this.padExactMql && this.padExactMqlHandler) {
      if (this.padExactMql.removeEventListener) {
        this.padExactMql.removeEventListener("change", this.padExactMqlHandler);
      } else {
        // @ts-ignore
        this.padExactMql.removeListener(this.padExactMqlHandler);
      }
    }

    if (this.desktopMql && this.desktopMqlHandler) {
      if (this.desktopMql.removeEventListener) {
        this.desktopMql.removeEventListener("change", this.desktopMqlHandler);
      } else {
        // @ts-ignore
        this.desktopMql.removeListener(this.desktopMqlHandler);
      }
    }

    this.mobileMql = null;
    this.mobileMqlHandler = null;
    this.padMql = null;
    this.padMqlHandler = null;
    this.padExactMql = null;
    this.padExactMqlHandler = null;
    this.desktopMql = null;
    this.desktopMqlHandler = null;
  }

  initIntersectionObserver() {
    const config = this.getConfig();
    if (!this.hasAutoplay(config)) return;
    this.isInViewport = false;

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const wasInViewport = this.isInViewport;
          this.isInViewport = entry.isIntersecting;

          if (this.swiperInstance && this.swiperInstance.autoplay) {
            if (config.responsiveAutoplay) {
              this.syncResponsiveAutoplay(config);
            } else if (this.isInViewport && !wasInViewport) {
              this.swiperInstance.autoplay.start();
            } else if (!this.isInViewport && wasInViewport) {
              this.swiperInstance.autoplay.stop();
            }
          }
        });
      },
      {
        threshold: 0,
        rootMargin: "0px",
      },
    );

    this.intersectionObserver.observe(this);
  }

  /**
   * @param {any} config
   */
  hasAutoplay(config) {
    return config.responsiveAutoplay
      ? Boolean(config.autoplayMobile || config.autoplayDesktop)
      : Boolean(config.autoplay);
  }

  /**
   * @param {any} config
   */
  isAutoplayEnabledForViewport(config) {
    if (!config.responsiveAutoplay) return Boolean(config.autoplay);
    return this.isMobileViewport()
      ? Boolean(config.autoplayMobile)
      : Boolean(config.autoplayDesktop);
  }

  /**
   * @param {any} [config]
   */
  syncResponsiveAutoplay(config = this.getConfig()) {
    if (!config.responsiveAutoplay) return;

    const swiper = this.swiperInstance;
    if (!swiper?.autoplay) return;

    const shouldRun =
      this.isInViewport &&
      this.isAutoplayEnabledForViewport(config) &&
      (Boolean(config.loop) || !swiper.isEnd);

    if (shouldRun && !swiper.autoplay.running) {
      swiper.autoplay.start();
    } else if (!shouldRun && swiper.autoplay.running) {
      swiper.autoplay.stop();
    }

    const paginationConfig = {
      ...config,
      autoplay: this.isAutoplayEnabledForViewport(config),
    };
    this.setupPagination(this.getElements().pagination, paginationConfig);
  }

  /**
   * @param {any} swiper
   * @param {Element | null} nextEl
   * @param {any} config
   */
  stopResponsiveAutoplayAtEnd(swiper, nextEl, config) {
    if (swiper.params?.loop || !swiper.isEnd) return;

    if (nextEl instanceof HTMLElement) {
      nextEl.classList.add("swiper-button-disabled");
    }

    if (this.isAutoplayEnabledForViewport(config)) {
      swiper.autoplay?.stop();
    }
  }

  destroyIntersectionObserver() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
    this.isInViewport = false;
  }

  applyDisabledDesktopState() {
    const elements = this.getElements();
    if (elements.pagination instanceof HTMLElement)
      elements.pagination.style.display = "none";
    if (elements.prev instanceof HTMLElement)
      elements.prev.style.display = "none";
    if (elements.next instanceof HTMLElement)
      elements.next.style.display = "none";
    if (elements.btnBox) {
      // @ts-ignore
      elements.btnBox.style.visibility = "hidden";
      // @ts-ignore
      elements.btnBox.style.top = "";
    }
  }

  syncSwiperWithViewport() {
    const config = this.getConfig();

    if (!config.onlyMobile && !config.onlyPad && !config.onlyPadExact && !config.onlyDesktop) {
      this.initSwiper();
      return;
    }

    if (config.onlyMobile) {
      if (this.isMobileViewport()) {
        this.initSwiper();
      } else {
        this.destroySwiper();
        this.applyDisabledDesktopState();
      }
      return;
    }

    if (config.onlyPad) {
      if (this.isPadViewport()) {
        this.initSwiper();
      } else {
        this.destroySwiper();
        this.applyDisabledDesktopState();
      }
      return;
    }

    if (config.onlyPadExact) {
      if (this.isPadExactViewport()) {
        this.initSwiper();
      } else {
        this.destroySwiper();
        this.applyDisabledDesktopState();
      }
      return;
    }

    if (config.onlyDesktop) {
      if (this.isDesktopViewport()) {
        this.initSwiper();
      } else {
        this.destroySwiper();
        this.applyDisabledDesktopState();
      }
    }
  }

  getElements() {
    return {
      pagination: this.querySelector(".swiper-pagination"),
      prev: this.querySelector(".swiper-button-prev"),
      next: this.querySelector(".swiper-button-next"),
      btnBox: this.querySelector(".spx-swiper-btn-box"),
    };
  }

  /**
   * @param {Element} container
   */
  getSlideCount(container) {
    const wrapper = container.querySelector(".swiper-wrapper");
    if (!wrapper) return 0;
    // 使用更精确的选择器，只查找当前 wrapper 内的直接子 slide
    return Array.from(wrapper.children).filter(
      (child) => child.classList && child.classList.contains("swiper-slide"),
    ).length;
  }

  updatePosition() {
    const elements = this.getElements();
    const btnBox = elements.btnBox;
    // @ts-ignore
    if (!btnBox || !(btnBox instanceof HTMLElement)) return;

    const refEl = this.getHeightRefElement();

    // @ts-ignore
    if (refEl && refEl instanceof HTMLElement) {
      // 使用 getBoundingClientRect 计算相对位置，比 offsetHeight/offsetTop 更准确
      const containerRect = this.getBoundingClientRect();
      const refRect = refEl.getBoundingClientRect();

      // 计算 refEl 中心相对于 container 顶部的距离
      const relativeTop = refRect.top - containerRect.top;
      const center = relativeTop + refRect.height / 2;

      if (center > 0) {
        // @ts-ignore
        btnBox.style.top = `${center}px`;
        // @ts-ignore
        btnBox.style.visibility = "visible";
      }
    }
  }

  /**
   * @param {Element} container
   * @param {string | null} overflow
   */
  applyOverflow(container, overflow) {
    if (overflow && container instanceof HTMLElement)
      container.style.overflow = overflow;
  }

  /**
   * @param {Element | null} el
   * @param {any} config
   */
  setupPagination(el, config) {
    if (!(el instanceof HTMLElement)) return;
    el.style.setProperty("--spx-swiper-progress", "0.00");
    if (config.autoplay) {
      el.classList.add("autoplay");
    } else {
      el.classList.remove("autoplay");
    }
  }

  /**
   * @param {any} elements
   * @param {any} config
   */
  setupVisibility(elements, config) {
    if (elements.pagination) {
      elements.pagination.style.display = config.pagination ? "" : "none";
    }
    if (elements.prev) {
      elements.prev.style.display = config.navigation ? "" : "none";
    }
    if (elements.next) {
      elements.next.style.display = config.navigation ? "" : "none";
    }
  }

  /**
   * @param {any} config
   * @param {any} elements
   */
  updateLockedVisibility(config, elements) {
    if (!this.swiperInstance) return;

    const swiper = this.swiperInstance;
    const loopEnabled = Boolean(swiper.params?.loop);
    const locked =
      !loopEnabled &&
      (Boolean(swiper.isLocked) ||
        (Array.isArray(swiper.snapGrid) && swiper.snapGrid.length <= 1));

    if (locked) {
      if (elements.pagination instanceof HTMLElement)
        elements.pagination.style.display = "none";
      if (elements.prev instanceof HTMLElement)
        elements.prev.style.display = "none";
      if (elements.next instanceof HTMLElement)
        elements.next.style.display = "none";
      if (elements.btnBox instanceof HTMLElement) {
        elements.btnBox.style.visibility = "hidden";
        elements.btnBox.style.top = "";
      }
      return;
    }

    this.setupVisibility(elements, config);
  }

  /**
   * @param {Element | null} paginationEl
   */
  getAutoplayEvents(paginationEl) {
    if (!(paginationEl instanceof HTMLElement)) return undefined;
    return {
      autoplayTimeLeft: (
        /** @type {any} */ _swiper,
        /** @type {number} */ _time,
        /** @type {number} */ progress,
      ) => {
        const value = 1 - progress;
        const clamped = Math.max(0, Math.min(1, value));
        paginationEl.style.setProperty(
          "--spx-swiper-progress",
          clamped.toFixed(2),
        );
      },
      slideChange: () => {
        paginationEl.style.setProperty("--spx-swiper-progress", "0.00");
      },
    };
  }

  /**
   * @param {string | undefined | null} val
   * @param {any} fallback
   * @returns {any}
   */
  parseValue(val, fallback) {
    if (val === undefined || val === null || val === "") return fallback;
    if (val === "true") return true;
    if (val === "false") return false;
    if (val === "auto") return "auto";
    if (val === "null") return null;
    const num = Number(val);
    return isNaN(num) ? val : num;
  }
}

if (!customElements.get("spx-slider")) {
  customElements.define("spx-slider", SpxSlider);
}

class SpxColorModalImageSlider {
  constructor() {
    /** @type {HTMLElement | null} */
    this.modalRoot = document.getElementById("spx-color-dialog");
    /** @type {HTMLElement | null} */
    this.activeDialog = null;
    /** @type {SpxSlider | null} */
    this.activeSliderEl = null;
    /** @type {string[]} */
    this.images = [];
    this.init();
  }

  init() {
    document.addEventListener(
      "spx:modal:open",
      this.handleModalOpen.bind(this),
    );
  }

  /**
   * @param {Event & { detail?: { rawData?: { productId?: string | number } } }} event
   */
  handleModalOpen(event) {
    const target = event.target instanceof Element ? event.target : null;
    const dialog =
      target?.closest?.("dialog") || this.modalRoot?.querySelector("dialog");
    if (!dialog) return;

    const productId =
      dialog.dataset.productId || event.detail?.rawData?.productId;
    const trigger = this.findTriggerByProductId(productId);
    if (!trigger) return;

    const images = this.getImagesFromTrigger(trigger);
    if (images.length === 0) return;

    this.activeDialog = dialog;
    this.activeSliderEl = dialog.querySelector("[data-color-modal-slider]");
    this.images = images;

    this.renderSlides();
    this.resetAndInitSwiper();
  }

  /**
   * @param {string | number | undefined} productId
   */
  findTriggerByProductId(productId) {
    if (!productId) return null;
    const triggers = document.querySelectorAll(
      ".spx-color-trigger[data-product-id]",
    );
    for (const trigger of triggers) {
      if (
        trigger instanceof HTMLElement &&
        trigger.dataset.productId === String(productId)
      ) {
        return trigger;
      }
    }
    return null;
  }

  /**
   * @param {HTMLElement} trigger
   */
  getImagesFromTrigger(trigger) {
    let imageNodes = trigger.querySelectorAll(".binding-images img");

    if (!imageNodes.length) {
      const prevEl = trigger.previousElementSibling;
      if (
        prevEl instanceof HTMLElement &&
        prevEl.classList.contains("binding-images")
      ) {
        imageNodes = prevEl.querySelectorAll("img");
      }
    }

    if (!imageNodes.length) {
      const nearbyBinding =
        trigger.parentElement?.querySelector(".binding-images");
      if (nearbyBinding) {
        imageNodes = nearbyBinding.querySelectorAll("img");
      }
    }

    return Array.from(imageNodes)
      .map((img) => {
        const imageEl = /** @type {HTMLImageElement} */ (img);
        return imageEl.currentSrc || imageEl.getAttribute("src") || "";
      })
      .filter(Boolean);
  }

  renderSlides() {
    const wrapper = this.activeDialog?.querySelector("[data-carousel-wrapper]");
    if (!wrapper) return;

    wrapper.innerHTML = this.images
      .map((src) => {
        return `<div class="swiper-slide"><img class="w-full aspect-square object-cover rounded-[12px]" src="${src}" alt="" loading="lazy" /></div>`;
      })
      .join("");
  }

  resetAndInitSwiper() {
    const slider = this.activeSliderEl;
    if (!slider) return;

    slider.destroySwiper?.();
    slider.swiperInstance = null;
    slider.syncSwiperWithViewport?.();

    const bind = () => {
      const swiper = slider.swiperInstance;
      if (!swiper) {
        requestAnimationFrame(bind);
        return;
      }

      swiper.slideTo(0, 0);
    };

    bind();
  }
}

new SpxColorModalImageSlider();
